import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {HorribleHeroesEvents} from "../events/HorribleHeroesEvents";
import {HorribleHeroesParsedInitData} from "../server/data/HorribleHeroesParsedInitData";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {HorribleHeroesLayerConfig} from "../config/IHorribleHeroesLayerConfig";
import {BalanceEvent} from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {HorribleHeroesTextStyles} from "../config/HorribleHeroesTextStyles";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SoundManager} from "../resource/sound/SoundManager";
import {HHTranslationStrings} from "./HHTranslationStrings";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {HorribleHeroes} from "../HorribleHeroes";

export class HeroesIntroView extends BaseView {
    private _layer: ScalableLayer;
    private _isRestore: boolean = true;
    private _soundTween: TweenLite;

    constructor() {
        super();
        this._layer = StageManager.getLayer(HorribleHeroesLayerConfig.HEROES_INTRO.name);
        this.reset();
    }

    private static getRandomSymbol(): string {
        let randomNum: number = Math.floor(Math.random() * (HorribleHeroesGameConfig.instance.HIGHMSYMS.length - 1));
        return (HorribleHeroesGameConfig.instance.HIGHMSYMS[randomNum]).toLowerCase();
    }

    protected addEventListeners(): void {
        EventHandler.addEventListener(this, HorribleHeroesEvents.ON_GAME_START, () => this.onGameStart());
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, BalanceEvent.BET, () => this.onBet());
    }

    protected initAnimations(): void {
        this.createSpine();
    }

    private createSpine(): void {
        let spineTexture: PIXI.spine.core.SkeletonData = GameResources.getSpineAsset("heroesIntroSpine"),
            spine: PIXI.spine.Spine = new PIXI.spine.Spine(spineTexture);

        spine.position.set(360, 360);
        spine.name = "randomHeroes";
        spine.visible = false;

        spine.state.addListener({
            complete: () => {
                this.reset(true);
            }
        });

        this._layer.addChild(spine);
    }

    private playRandomSpine(symName: string): void {
        if (symName) {
            this.getRandomText(symName);
            const animName: string = "intro_" + symName;
            let spine: PIXI.spine.Spine = this.getSpine();
            spine.updateTransform();
            spine.visible = true;
            spine.state.setAnimation(0, animName, false);

            // #452
            const delay: number = spine.spineData.findAnimation(animName).duration / 2;
            SoundManager.playIntroSplashIn();
            this._soundTween = TweenLite.delayedCall(delay, () => SoundManager.playIntroSplashOut());
        }
    }

    private onGameStart(): void {
        if (!(this._isRestore || (GameSetting.replayMode))) {
            const tl: TimelineLite = new TimelineLite();
            tl.add(() => {
                let randomSymbol: string = HeroesIntroView.getRandomSymbol();
                this.playRandomSpine(randomSymbol);
            }, 0.5); //delay to show heroes intro on game start
            tl.play(0);
        }

        this._isRestore = true; // resetting it, since we only want to play this once when the game is loaded first time normally.
    }

    private onInitDataParsed(data: HorribleHeroesParsedInitData): void {
        this._isRestore = data.isRestoreState;

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private onBet(): void {
        this.reset(true);
    }

    private getSpine(): PIXI.spine.Spine {
        return (this._layer.getChildByName("randomHeroes")) as PIXI.spine.Spine;
    }

    private getContainer(): PIXI.Container {
        return (this._layer.getChildByName("quoteContainer")) as PIXI.Container;
    }

    private getTextElement(): PIXI.Text {
        let quoteContainer: PIXI.Container = this.getContainer();
        return (quoteContainer.getChildByName("quoteText")) as PIXI.Text;
    }

    private reset(withDelay: boolean = false): void {
        let timeDuration: number = withDelay ? 0.3 : 0,
            spine: PIXI.spine.Spine = this.getSpine(),
            quoteContainer: PIXI.Container = this.getContainer();

        // #452
        if (this._soundTween) {
            this._soundTween.pause();
            this._soundTween.kill();
        }

        if (spine) {
            TweenLite.to(spine, timeDuration, {
                alpha: 0,
                onComplete: () => {
                    spine.state.clearTracks();
                    spine.visible = false;
                }
            });
        }

        if (quoteContainer) {
            quoteContainer.position.set(0, 575);
            quoteContainer.alpha = 0;
            let quoteText: PIXI.Text = this.getTextElement();

            if (quoteText) {
                quoteText.text = "";
            }
        }
    }

    private showTextBox(quoteString: string): void {
        let quoteContainer: PIXI.Container = this.getContainer();

        if (!quoteContainer) {
            this.createTextBox();
            this.showTextBox(quoteString);
        } else {
            let quoteText: PIXI.Text = this.getTextElement();

            quoteText.text = quoteString;
            this.animateQuotedText();
        }
    }

    private animateQuotedText(): void {
        this.adjustText();
        let quoteContainer: PIXI.Container = this.getContainer();

        quoteContainer.position.set(0, 575);
        quoteContainer.alpha = 0.3;

        TweenLite.to(quoteContainer, 0.166, {alpha: 1});
        TweenLite.to(quoteContainer.position, 0.166, {
            x: 350,
            ease: Linear.easeOut,
            onComplete: () => {
                TweenLite.to(quoteContainer.position, 1.833, {
                    x: 400,
                    ease: Linear.easeOut,
                    onComplete: () => {
                        TweenLite.to(quoteContainer.position, 0.33, {x: 500});
                        TweenLite.to(quoteContainer, 0.33, {alpha: 0});
                    }
                });
            }
        });
    }

    private adjustText(): void {
        let quoteText: PIXI.Text = this.getTextElement(),
            textMetrics = PIXI.TextMetrics.measureText(quoteText.text, HorribleHeroesTextStyles.HEROES_INTRO_QUOTE_FONT),
            bgMaxWidth: number = 250,
            maxLineW: number = Math.ceil(textMetrics.maxLineWidth);

        //for increasing font size for text SMASH!
        if (textMetrics.text.indexOf("SMASH") > -1) {
            if (maxLineW < (bgMaxWidth * 0.5)) {
                quoteText.style.fontSize++;
                quoteText.updateTransform();
                this.adjustText();
            }
        } else {
            quoteText.style.fontSize = 16;
            quoteText.updateTransform();
        }
    }

    private createTextBox(): void {
        let quoteContainer: PIXI.Container = new PIXI.Container(),
            quoteText: PIXI.Text = new PIXI.Text("", HorribleHeroesTextStyles.HEROES_INTRO_QUOTE_FONT),
            quoteBgSprite: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("quoteBgSprite")[0]);

        quoteText.name = "quoteText";
        quoteText.anchor.set(0.5);

        quoteBgSprite.position.set(0, 2);
        quoteBgSprite.anchor.set(0.5);
        quoteBgSprite.name = "quoteBgSprite";

        quoteContainer.name = "quoteContainer";
        quoteContainer.position.set(0, 575);
        quoteContainer.alpha = 0;
        quoteContainer.addChild(quoteBgSprite, quoteText);

        this._layer.addChild(quoteContainer);
    }

    private getRandomText(symName: string): void {
        let quoteText: string[];
        switch (symName) {
            case "m1":
            default:
                quoteText = [
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M1quote0),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M1quote1),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M1quote2)
                ];

                break;
            case "m2":
                quoteText = [
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M2quote0),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M2quote1)
                ];

                break;
            case "m3":
                quoteText = [
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M3quote0),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M3quote1),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M3quote2),
                ];

                break;
            case "m4":
                quoteText = [
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M4quote0),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M4quote1),
                ];

                break;
            case "m5":
                quoteText = [
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M5quote0),
                    HorribleHeroes.api.translations.translate(HHTranslationStrings.M5quote1),
                ];

                break;
        }

        let randomNum: number = Math.floor(Math.random() * quoteText.length),
            quoteStr: string = quoteText[randomNum];

        this.showTextBox(quoteStr);
    }
}